import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getFavorites, addToFavorites as addToServerFavorites, removeFromFavorites as removeFromServerFavorites } from '../services/favoriteService';
import { toast } from 'react-hot-toast';

const FavoritesContext = createContext();

export const useFavorites = () => {
  return useContext(FavoritesContext);
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Kullanıcı değiştiğinde favorileri yükle
  useEffect(() => {
    const loadFavorites = async () => {
      setLoading(true);
      try {
        if (user) {
          // Giriş yapmış kullanıcı için sunucudan favorileri al
          const data = await getFavorites(user.id);
          setFavorites(data || []);
        } else {
          // Misafir kullanıcı için localStorage'dan favorileri al
          const guestFavorites = JSON.parse(localStorage.getItem('guestFavorites') || '[]');
          setFavorites(guestFavorites);
        }
      } catch (error) {
        toast.error('Favoriler yüklenirken bir hata oluştu');
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    const handleGuestDataTransferred = async (event) => {
      const { favoritesTransferred } = event.detail;
      
      if (user) {
        // Her zaman favorileri yeniden yükle (transfer olsun ya da olmasın)
        try {
          const data = await getFavorites(user.id);
          setFavorites(data || []);
        } catch (error) {
          // Sessizce hata yönetimi
        }
      }
    };

    loadFavorites();

    // Guest data transfer event listener'ını ekle
    if (user) {
      window.addEventListener('guestDataTransferred', handleGuestDataTransferred);
    }

    // Cleanup function
    return () => {
      if (user) {
        window.removeEventListener('guestDataTransferred', handleGuestDataTransferred);
      }
    };
  }, [user]);

  const addToFavorites = async (product) => {
    try {
      if (user) {
        // Giriş yapmış kullanıcı için sunucuya kaydet
        const favoriteData = {
          user_id: user.id,
          product_id: product.id,
          phone_models: product.phone_models || []
        };

        await addToServerFavorites(favoriteData);
        const updatedFavorites = await getFavorites(user.id);
        setFavorites(updatedFavorites || []);
      } else {
        // Misafir kullanıcı için localStorage'a kaydet
        const guestFavorites = JSON.parse(localStorage.getItem('guestFavorites') || '[]');
        
        // Ürün zaten favorilerde mi kontrol et
        if (guestFavorites.some(fav => fav.product.id === product.id)) {
          toast.error('Bu ürün zaten favorilerinizde');
          return;
        }

        // Yeni yapıya göre favori oluştur
        const newFavorite = {
          id: Date.now(), // Geçici bir ID
          product: product,
          phone_models: product.phone_models || []
        };

        const updatedFavorites = [...guestFavorites, newFavorite];
        localStorage.setItem('guestFavorites', JSON.stringify(updatedFavorites));
        setFavorites(updatedFavorites);
      }
    } catch (error) {
      toast.error('Favorilere eklenirken bir hata oluştu');
      throw error;
    }
  };

  const removeFromFavorites = async (productId) => {
    try {
      if (user) {
        // Giriş yapmış kullanıcı için sunucudan sil
        await removeFromServerFavorites(user.id, productId);
        const updatedFavorites = await getFavorites(user.id);
        setFavorites(updatedFavorites || []);
      } else {
        // Misafir kullanıcı için localStorage'dan sil
        const guestFavorites = JSON.parse(localStorage.getItem('guestFavorites') || '[]');
        const updatedFavorites = guestFavorites.filter(fav => fav.product.id !== productId);
        localStorage.setItem('guestFavorites', JSON.stringify(updatedFavorites));
        setFavorites(updatedFavorites);
      }
    } catch (error) {
      toast.error('Favorilerden kaldırılırken bir hata oluştu');
      throw error;
    }
  };

  const value = {
    favorites,
    loading,
    addToFavorites,
    removeFromFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}; 